import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import { Button, Code, Heading, Text } from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { toAbsoluteMoment } from 'src/components/affectedOperations/affectedOperationsHelpers';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { format } from 'src/lib/format';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const OperationsListBreadcrumb = ({
  operationId,
  operationName,
  onViewOperationBodyClick,
  prevOperationId,
  nextOperationId,
  createdAt,
  from,
  to,
}: {
  operationId?: string;
  operationName?: string | null;
  onViewOperationBodyClick: () => void;
  prevOperationId: string | undefined;
  nextOperationId: string | undefined;
  createdAt: Timestamp['output'] | null;
  from: Timestamp['output'] | null | undefined;
  to: Timestamp['output'] | null | undefined;
}) => {
  const location = useLocation();
  const { currentOperationsCheckDetailsRoute } = useInternalGraphLinking();

  let timeFrom = null;
  let timeTo = null;

  if (createdAt && from && to) {
    timeFrom = toAbsoluteMoment(from, createdAt);
    timeTo = toAbsoluteMoment(to, createdAt);
  }

  if (!operationId) return null;

  return (
    <>
      <div className="flex items-start justify-between pb-4">
        {operationId && operationName ? (
          <div className="mr-6 flex min-w-[300px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <Heading className="truncate" size="2xl">
                {operationName}
              </Heading>{' '}
              <Code variant="mono">{format.shortQueryId(operationId)}</Code>
            </div>
            {timeFrom && timeTo && (
              <Text>
                Timeframe checked between{' '}
                <LocalTimestamp className="text-body-sb" value={timeFrom} />
                {' — '}
                <LocalTimestamp className="text-body-sb" value={timeTo} />
              </Text>
            )}
          </div>
        ) : null}
        <div className="flex flex-wrap-reverse justify-end gap-3">
          {operationId && (
            <>
              <Button
                variant="secondary"
                as={Link}
                leftIcon={<IconArrowLeft />}
                to={
                  prevOperationId &&
                  currentOperationsCheckDetailsRoute.locationFrom({
                    location,
                    patch: { query: prevOperationId },
                  })
                }
                isDisabled={!prevOperationId}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                as={Link}
                rightIcon={<IconArrowRight />}
                to={
                  nextOperationId &&
                  currentOperationsCheckDetailsRoute.locationFrom({
                    location,
                    patch: { query: nextOperationId },
                  })
                }
                isDisabled={!nextOperationId}
              >
                Next
              </Button>
            </>
          )}
          {operationId && operationName && (
            <Button
              variant="secondary"
              as={Link}
              to={setOverlayVisibleLocation({
                location,
                overlayName: 'operation-details',
              })}
              onClick={onViewOperationBodyClick}
            >
              View Operation Body
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
